/**
 * Maps URL pathnames to their corresponding MidjenRouteTracePolicy.
 *
 * This enables automatic trace policy assignment for navigation tracking
 * without requiring manual policy setting on each page.
 */

import { MidjenRouteTracePolicy } from '@/app/types/analytics';

/**
 * Route configuration for pathname to trace policy mapping.
 * Routes are matched in order, so more specific patterns should come first.
 */
interface RouteConfig {
  /** Regex pattern to match against the pathname */
  pattern: RegExp;
  /** The trace policy to assign for matching routes */
  policy: MidjenRouteTracePolicy;
}

/**
 * Route configurations ordered by specificity (most specific first).
 */
const ROUTE_CONFIGS: RouteConfig[] = [
  // Pro routes
  { pattern: /^\/create\/?$/, policy: MidjenRouteTracePolicy.PRO_CREATE },
  { pattern: /^\/edit\/?/, policy: MidjenRouteTracePolicy.PRO_EDIT },

  // Project routes (canonical URLs)
  {
    pattern: /^\/projects\/[^/]+\/content\/[^/]+\/?$/,
    policy: MidjenRouteTracePolicy.PROJECT_CONTENT_EDIT,
  },
  {
    pattern: /^\/projects\/content\/[^/]+\/?$/,
    policy: MidjenRouteTracePolicy.PROJECT_CONTENT_EDIT,
  },
  { pattern: /^\/projects\/[^/]+\/?$/, policy: MidjenRouteTracePolicy.PROJECT_DETAIL },
  { pattern: /^\/projects\/?$/, policy: MidjenRouteTracePolicy.PROJECTS },

  // Media Library routes
  { pattern: /^\/media\/?$/, policy: MidjenRouteTracePolicy.MEDIA_LIBRARY },

  // User routes
  { pattern: /^\/account\/?$/, policy: MidjenRouteTracePolicy.ACCOUNT },

  // Admin routes
  { pattern: /^\/admin\/?/, policy: MidjenRouteTracePolicy.ADMIN },

  // Deep link landing pages
  { pattern: /^\/space\/[^/]+\/?$/, policy: MidjenRouteTracePolicy.SPACE_INVITE },

  // Story Mode routes (specific first)
  { pattern: /^\/stories\/[^/]+\/?$/, policy: MidjenRouteTracePolicy.STORY_DETAIL },
  { pattern: /^\/stories\/?$/, policy: MidjenRouteTracePolicy.STORIES },

  // Other routes
  {
    pattern: /^\/playables\/preview\/[^/]+\/?$/,
    policy: MidjenRouteTracePolicy.PLAYABLE_PREVIEW_PAGE,
  },
  { pattern: /^\/playables\/?/, policy: MidjenRouteTracePolicy.PLAYABLES },
  { pattern: /^\/stats\/?$/, policy: MidjenRouteTracePolicy.STATS },
  { pattern: /^\/import\/?/, policy: MidjenRouteTracePolicy.IMPORT },
  { pattern: /^\/assets\/?$/, policy: MidjenRouteTracePolicy.CREATE },

  // Main routes (catch-all should be last)
  { pattern: /^\/$/, policy: MidjenRouteTracePolicy.HOME },
];

/**
 * Get the trace policy for a given pathname.
 *
 * @param pathname - The URL pathname to map (e.g., "/moodboards/123")
 * @returns The matching MidjenRouteTracePolicy, or null if no match
 *
 * @example
 * ```ts
 * getTracePolicyFromPathname('/'); // MidjenRouteTracePolicy.HOME
 * getTracePolicyFromPathname('/unknown'); // null
 * ```
 */
export function getTracePolicyFromPathname(pathname: string): MidjenRouteTracePolicy | null {
  for (const config of ROUTE_CONFIGS) {
    if (config.pattern.test(pathname)) {
      return config.policy;
    }
  }
  return null;
}
